import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
import { OperationalStatus, ResourceCategory } from './resources.js';
/** Recherche de disponibilité (moteur central, FR-AVL-01). */
export const AvailabilityQuery = z
    .object({
    propertyId: Uuid.optional(),
    from: IsoDateTime,
    to: IsoDateTime,
    typeCode: z.string().optional(),
    category: ResourceCategory.optional(),
    capacity: z.coerce.number().int().positive().optional(),
    /** Ignorer ce bloc (ex. la réservation en cours de modification). */
    excludeReservationId: Uuid.optional(),
})
    .refine((v) => new Date(v.to) > new Date(v.from), {
    message: 'to doit être postérieur à from',
    path: ['to'],
});
export const ConflictSource = z.enum(['RESERVATION', 'BLOCK']);
export const AvailabilityConflict = z.object({
    source: ConflictSource,
    sourceId: Uuid,
    /** Ressource réellement réservée/bloquée (peut être le parent ou un enfant — ADR-013). */
    resourceId: Uuid,
    resourceName: z.string(),
    unitCode: z.string(),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    label: z.string().nullable(),
});
/**
 * `details` d'une erreur 409 `RESOURCE_NOT_AVAILABLE`.
 * `conflicts` est la liste exploitable par l'UI (ressource réellement occupée,
 * unité d'inventaire, période) ; `unitCodes` et `requestedPeriod` la résument.
 * `conflicts` peut être vide lorsque le refus vient de la contrainte d'exclusion
 * PostgreSQL (course entre deux transactions concurrentes).
 */
export const ResourceNotAvailableDetails = z.object({
    requestedPeriod: z.object({ startsAt: IsoDateTime, endsAt: IsoDateTime }),
    unitCodes: z.array(z.string()),
    conflicts: z.array(AvailabilityConflict),
});
export const ResourceAvailability = z.object({
    resourceId: Uuid,
    resourceName: z.string(),
    typeCode: z.string(),
    capacityMax: z.number().int(),
    parentResourceId: Uuid.nullable(),
    available: z.boolean(),
    conflicts: z.array(AvailabilityConflict),
    defaultRate: z
        .object({ pricingUnit: z.string(), price: z.number(), currencyCode: z.string() })
        .nullable(),
});
/** Vue calendrier (FR-AVL-03). */
export const CalendarQuery = z
    .object({
    propertyId: Uuid.optional(),
    from: IsoDateTime,
    to: IsoDateTime,
    typeCode: z.string().optional(),
})
    .refine((v) => new Date(v.to) > new Date(v.from), {
    message: 'to doit être postérieur à from',
    path: ['to'],
});
export const CalendarEventKind = z.enum(['RESERVATION', 'BLOCK', 'DERIVED_UNAVAILABLE']);
/**
 * Valeurs de `CalendarEvent.status` pour le kind `DERIVED_UNAVAILABLE` (ADR-013).
 * `PARENT_BOOKED` : un ancêtre (logement entier) est occupé ;
 * `CHILD_BOOKED` : une composante (chambre) l'est.
 */
export const DerivedUnavailabilityStatus = z.enum(['PARENT_BOOKED', 'CHILD_BOOKED']);
export const CalendarEvent = z.object({
    id: z.string(),
    kind: CalendarEventKind,
    resourceId: Uuid,
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    /**
     * Statut de réservation ou kind de blocage ; pour `DERIVED_UNAVAILABLE`,
     * une valeur de `DerivedUnavailabilityStatus`.
     */
    status: z.string(),
    label: z.string(),
    sublabel: z.string().nullable(),
    reservationId: Uuid.nullable(),
    blockId: Uuid.nullable(),
    /** Ressource à l'origine de l'indisponibilité dérivée. */
    originResourceId: Uuid.nullable(),
});
export const CalendarRow = z.object({
    resourceId: Uuid,
    name: z.string(),
    internalRef: z.string().nullable(),
    typeCode: z.string(),
    typeName: z.string(),
    category: ResourceCategory,
    parentResourceId: Uuid.nullable(),
    /** Étage, pour regrouper les lignes du planning. */
    floor: z.number().int().nullable(),
    capacityMax: z.number().int(),
    operationalStatus: OperationalStatus,
    defaultRate: z
        .object({ pricingUnit: z.string(), price: z.number(), currencyCode: z.string() })
        .nullable(),
    events: z.array(CalendarEvent),
});
export const CalendarResponse = z.object({
    from: IsoDateTime,
    to: IsoDateTime,
    rows: z.array(CalendarRow),
});
//# sourceMappingURL=availability.js.map